<template>
  <div class="online_order_card">
    <!-- Header -->
    <div :class="['online_order_card__header', headerClass]">
      {{ headerText }}
    </div>

    <!-- Body -->
    <div class="online_order_card__body">
      <!-- 訂單編號 + 展開箭頭 -->
      <div class="online_order_card__order_id" @click="expanded = !expanded">
        <span>{{ $t('OrderFoodManagement.OnlineOrderLabel') }} {{ displayComOrderId }}</span>
        <i :class="expanded ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
      </div>

      <!-- 展開內容 -->
      <div v-show="expanded" class="online_order_card__detail">
        <!-- 餐點品項 -->
        <div class="online_order_card__items">
          <div
            v-for="(item, index) in displayItems"
            :key="index"
            class="online_order_card__item"
          >
            {{ item }}
          </div>
        </div>

        <!-- 戶別 -->
        <div class="online_order_card__household">
          {{ $t('OrderFoodManagement.OnlineOrderHousehold') }} {{ order.tabletNote || '-' }}
        </div>
      </div>

      <!-- 操作按鈕 -->
      <div class="online_order_card__actions">
        <template v-if="status === 'pending'">
          <el-button type="primary" size="small" @click="$emit('accept', order)">
            {{ $t('OrderFoodManagement.OnlineOrderActionAccept') }}
          </el-button>
          <el-button type="danger" size="small" plain @click="$emit('reject', order)">
            {{ $t('OrderFoodManagement.OnlineOrderActionReject') }}
          </el-button>
        </template>
        <template v-else-if="status === 'preparing'">
          <el-button type="primary" size="small" @click="$emit('notify-pickup', order)">
            {{ $t('OrderFoodManagement.OnlineOrderActionNotifyPickup') }}
          </el-button>
          <el-button type="primary" plain size="small" @click="$emit('reprint', order)">
            {{ $t('OrderFoodManagement.OnlineOrderActionReprint') }}
          </el-button>
          <el-button type="danger" size="small" plain @click="$emit('cancel', order)">
            {{ $t('OrderFoodManagement.OnlineOrderActionCancel') }}
          </el-button>
        </template>
        <template v-else-if="status === 'pickup'">
          <el-button type="primary" size="small" @click="$emit('complete-pickup', order)">
            {{ $t('OrderFoodManagement.OnlineOrderActionCompletePickup') }}
          </el-button>
          <el-button type="primary" plain size="small" @click="$emit('notify-pickup-again', order)">
            {{ $t('OrderFoodManagement.OnlineOrderActionNotifyPickupAgain') }}
          </el-button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { formatOrderDisplayCode, DiningOption } from '@/js/utils/orderFood'

export default {
  name: 'OnlineOrderCard',
  props: {
    /** 單筆訂單資料（已 camelCase 化） */
    order: {
      type: Object,
      required: true
    },
    /** 卡片所屬欄位狀態，決定 header 樣式與操作按鈕 */
    status: {
      type: String,
      required: true,
      validator: (val) => ['pending', 'preparing', 'pickup'].includes(val)
    }
  },
  data () {
    return {
      /** 是否展開餐點明細區段 */
      expanded: false
    }
  },
  computed: {
    /**
     * 卡片頂部文字
     * - 內用 (diningOption=1)：「內用：xxx號桌」
     * - 現場外帶 (diningOption=2)：「現場外帶」
     * - 預約 (diningOption=3)：「預約 yyyy/MM/dd HH:mm」
     */
    headerText () {
      const dining = this.order.dining
      if (!dining) return '-'
      // 內用
      if (dining.diningOption === DiningOption.DineIn) {
        return this.$t('OrderFoodManagement.OnlineOrderHeaderDineIn', { table: dining.tableNo || '-' })
      }
      // 預約 (diningOption === 3)
      const time = dining.reservationAtUtc
        ? dayjs(dining.reservationAtUtc).format('YYYY/MM/DD HH:mm')
        : '-'
      return this.$t('OrderFoodManagement.OnlineOrderHeaderReservation', { time })
    },
    /** 卡片頂部背景色 class：內用為綠色，其餘為主色 */
    headerClass () {
      const dining = this.order.dining
      if (dining && dining.diningOption === DiningOption.DineIn) return 'online_order_card__header--dine_in'
      return 'online_order_card__header--reservation'
    },
    /** 訂單編號顯示 */
    displayComOrderId () {
      return formatOrderDisplayCode(this.order.comOrderId, this.order.createdAtUtc)
    },
    /**
     * 格式化餐點品項列表
     * 格式：【類別名稱】 品項名稱（規格）：數量
     */
    displayItems () {
      if (!this.order.items || !this.order.items.length) return []
      return this.order.items.map(item => {
        const groupLabel = item.groupName ? `【${item.groupName}】` : ''
        const choosePart = item.chooseName ? `（${item.chooseName}）` : ''
        return `${groupLabel} ${item.itemName}${choosePart}：${item.quantity}`
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.online_order_card {
  width: 100%;
  background: #fff;
  border-radius: 8px;
  border: solid 1px #ccc;
  margin-bottom: 12px;
  overflow: hidden;

  &__header {
    padding: 16px;
    color: #fff;
    font-weight: 600;
    font-size: map-get($fontSize, x-large);
    text-align: left;

    &--reservation {
      background-color: #009cc4;
    }

    &--dine_in {
      background-color: $mainColorDark;
    }
  }

  &__body {
    padding: 12px 16px;
  }

  &__order_id {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    font-size: map-get($fontSize, large);
    font-weight: 600;
    margin-bottom: 20px;
    color: #333;

    i {
      font-size: map-get($fontSize, medium);
      color: #999;
    }
  }

  &__detail {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 20px;
  }

  &__items {
    margin-bottom: 24px;
    font-size: map-get($fontSize, medium);
    color: #333;
    text-align: left;
  }

  &__household {
    font-size: map-get($fontSize, medium);
    color: #666;
  }

  &__note {
    font-size: map-get($fontSize, x-small);
    color: #999;
    margin-bottom: 4px;
  }

  &__actions {
    display: flex;
    align-items: center;
  }
}
</style>
